import { useTheme } from '@emotion/react';
import cn from 'classnames';

import { IConDefaultType } from '../types';

export enum DefaultUserIconVariant {
  DEFAULT = 'default',
  NO_BORDER = 'no_border',
  CHAT_SIDE_MENU = 'chat_side_menu',
  CHARACTERS = 'characters',
}

interface IDefaultUserIconProps extends IConDefaultType {
  height?: number;
  variant?: DefaultUserIconVariant;
}

function DefaultUserIcon({
  color,
  className,
  size = 40,
  height,
  rotate,
  flip,
  variant = DefaultUserIconVariant.DEFAULT,
  style,
}: IDefaultUserIconProps) {
  const theme = useTheme();
  return (
    <i
      className={cn('dds-icon', className, { flip })}
      style={{
        width: size,
        minWidth: size,
        height: height ?? size,
        transform: `rotate(${rotate}deg)`,
        ...style,
      }}
    >
      {variant === DefaultUserIconVariant.DEFAULT && (
        <svg viewBox="0 0.503906 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
          <g id="default">
            <g clipPath="url(#clip0_13424_249989)">
              <rect y="0.503906" width="40" height="40" rx="20" fill={color || theme.color.popup.background} />
              <g id="Shape">
                <mask
                  id="mask0_13424_249989"
                  style={{ maskType: 'alpha' }}
                  maskUnits="userSpaceOnUse"
                  x="0"
                  y="0"
                  width="40"
                  height="41"
                >
                  <circle id="Ellipse 59" cx="20" cy="20.5039" r="20" fill={color || theme.color.popup.background} />
                </mask>
                <g mask="url(#mask0_13424_249989)">
                  <g id="Union">
                    <path
                      d="M19.9989 28.3239C25.0704 28.3239 29.1969 23.3662 29.1969 17.271C29.1969 11.6937 25.5867 8.08984 19.9989 8.08984C14.4125 8.08984 10.8023 11.6937 10.8023 17.271C10.8023 23.366 14.9285 28.3239 19.9989 28.3239Z"
                      fill="#D8D8D8"
                    />
                    <path
                      d="M16.4622 31.083C8.64752 31.083 2.3125 37.4181 2.3125 45.2327V53.015C2.3125 56.9223 5.48001 60.0898 9.38733 60.0898H30.6118C34.5191 60.0898 37.6867 56.9223 37.6867 53.015V45.2327C37.6867 37.4181 31.3516 31.083 23.537 31.083H16.4622Z"
                      fill="#D8D8D8"
                    />
                  </g>
                </g>
              </g>
            </g>
            <rect x="0.5" y="1.00391" width="39" height="39" rx="19.5" stroke={color || theme.color.line.line_2} />
          </g>
          <defs>
            <clipPath id="clip0_13424_249989">
              <rect y="0.503906" width="40" height="40" rx="20" fill={color || theme.color.popup.background} />
            </clipPath>
          </defs>
        </svg>
      )}
      {variant === DefaultUserIconVariant.NO_BORDER && (
        <svg viewBox="0 0.503906 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
          <g clipPath="url(#clip0_noborder)">
            <rect y="0.503906" width="40" height="40" rx="20" fill={color || theme.color.popup.background} />
            <mask
              id="mask0_noborder"
              style={{ maskType: 'alpha' }}
              maskUnits="userSpaceOnUse"
              x="0"
              y="0"
              width="40"
              height="41"
            >
              <circle cx="20" cy="20.5039" r="20" fill={color || theme.color.popup.background} />
            </mask>
            <g mask="url(#mask0_noborder)">
              <path
                d="M19.9989 28.3239C25.0704 28.3239 29.1969 23.3662 29.1969 17.271C29.1969 11.6937 25.5867 8.08984 19.9989 8.08984C14.4125 8.08984 10.8023 11.6937 10.8023 17.271C10.8023 23.366 14.9285 28.3239 19.9989 28.3239Z"
                fill="#D8D8D8"
              />
              <path
                d="M16.4622 31.083C8.64752 31.083 2.3125 37.4181 2.3125 45.2327V53.015C2.3125 56.9223 5.48001 60.0898 9.38733 60.0898H30.6118C34.5191 60.0898 37.6867 56.9223 37.6867 53.015V45.2327C37.6867 37.4181 31.3516 31.083 23.537 31.083H16.4622Z"
                fill="#D8D8D8"
              />
            </g>
          </g>
          <defs>
            <clipPath id="clip0_noborder">
              <rect y="0.503906" width="40" height="40" rx="20" fill={color || theme.color.popup.background} />
            </clipPath>
          </defs>
        </svg>
      )}
      {variant === DefaultUserIconVariant.CHAT_SIDE_MENU && (
        <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 48 48" fill="none">
          <g clipPath="url(#clip0_2200_360839)">
            <rect width="48" height="48" rx="24" fill={theme.color.field.background} />
            <mask
              id="mask0_2200_360839"
              style={{ maskType: 'luminance' }}
              maskUnits="userSpaceOnUse"
              x="-2"
              y="5"
              width="52"
              height="48"
            >
              <rect
                width="51.072"
                height="46.9533"
                rx="6.17806"
                transform="matrix(-1 0 0 1 49.5371 5.69141)"
                fill="white"
              />
            </mask>
            <g mask="url(#mask0_2200_360839)">
              <path
                d="M23.999 32.5454C29.9039 32.5454 34.7084 26.7731 34.7084 19.6763C34.7084 13.1825 30.505 8.98639 23.999 8.98639C17.4946 8.98639 13.2912 13.1825 13.2912 19.6763C13.2912 26.7728 18.0955 32.5454 23.999 32.5454Z"
                fill={theme.color.line.line_2}
              />
              <path
                d="M19.8811 35.758C10.7823 35.758 3.40625 43.134 3.40625 52.2328V61.294C3.40625 65.8434 7.09427 69.5314 11.6437 69.5314H36.3559C40.9053 69.5314 44.5933 65.8434 44.5933 61.294V52.2328C44.5933 43.134 37.2173 35.758 28.1185 35.758H19.8811Z"
                fill={theme.color.line.line_2}
              />
            </g>
          </g>
          <rect x="0.5" y="0.5" width="47" height="47" rx="23.5" stroke={theme.color.line.line_2} />
          <defs>
            <clipPath id="clip0_2200_360839">
              <rect width="48" height="48" rx="24" fill="white" />
            </clipPath>
          </defs>
        </svg>
      )}
      {variant === DefaultUserIconVariant.CHARACTERS && (
        <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 12 12" fill="none">
          <path
            d="M4.49805 1.5C5.74053 1.50012 6.74796 2.50742 6.74805 3.75C6.74805 4.5683 6.30937 5.28197 5.65625 5.67578C6.16752 5.79255 6.61906 5.98671 7.0127 6.23828C6.81854 5.95687 6.70215 5.61786 6.70215 5.25C6.70234 4.28347 7.48643 3.5 8.45312 3.5C9.41953 3.50034 10.2029 4.28368 10.2031 5.25C10.2031 5.88628 9.8623 6.44163 9.35449 6.74805C10.0321 6.90329 10.5765 7.23201 10.9893 7.66113C11.5647 8.25945 11.8698 9.03525 11.9492 9.7627C11.9955 10.1873 11.6436 10.4999 11.2666 10.5H0.881836C0.397376 10.4998 -0.0544148 10.0984 0.00488281 9.55273C0.10685 8.61739 0.499472 7.61989 1.23926 6.85059C1.7698 6.29894 2.46899 5.87634 3.33984 5.67676C2.6863 5.28304 2.24805 4.56862 2.24805 3.75C2.24814 2.50734 3.25546 1.5 4.49805 1.5ZM8.32715 7.58789C8.70615 8.20635 8.9224 8.89469 8.99414 9.55273C8.99464 9.55729 8.99469 9.56187 8.99512 9.56641H10.9727C10.8724 9.11009 10.6549 8.65959 10.3164 8.30762C9.91492 7.89038 9.31639 7.58306 8.45215 7.58301C8.40987 7.58301 8.36812 7.58622 8.32715 7.58789ZM4.5 6.5498C3.33623 6.5498 2.51471 6.96625 1.95996 7.54297C1.43545 8.08834 1.12727 8.80122 1.02051 9.5H7.97949C7.87274 8.80124 7.56453 8.08833 7.04004 7.54297C6.48532 6.96619 5.66377 6.54986 4.5 6.5498ZM8.45312 4.43359C8.00179 4.43359 7.63593 4.79904 7.63574 5.25C7.63574 5.70112 8.00167 6.06738 8.45312 6.06738C8.90429 6.06704 9.26953 5.70091 9.26953 5.25C9.26934 4.79926 8.90417 4.43394 8.45312 4.43359ZM4.49805 2.5C3.80782 2.5 3.24814 3.05956 3.24805 3.75C3.24805 4.44052 3.80776 5 4.49805 5C5.18823 4.99988 5.74805 4.44044 5.74805 3.75C5.74796 3.05963 5.18817 2.50012 4.49805 2.5Z"
            fill={color || theme.color.line.line_2}
          />
        </svg>
      )}
    </i>
  );
}

export default DefaultUserIcon;
